import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';

import SortableTable from 'components/sortable-table/SortableTable';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import AddNew from 'components/buttons/add-new/AddNew';
import Spinner from 'components/spinner/Spinner';
import { withModal } from 'components/modal/ModalHOC';

import SOVModalMeta from 'modules/insights/sov/components/modals/meta/SOVModalMeta';
import SOVModalGeneric from 'modules/insights/sov/components/modals/generic/SOVModalGeneric';
import SOVTeaserFreemium from 'modules/insights/sov/components/teasers/freemium/SOVTeaserFreemium';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { CREATE, DELETE, DUPLICATE, FIXED_TO_TOP, REPORT, UNTITLED } from 'modules/insights/sov/utils/sov';
import {
  ICONS_MAP,
  enrichReportIcon,
  getIconDelete,
  getIconDuplicate,
  getIconView
} from 'modules/insights/sov/utils/icons/sov-icons';
import {
  getColumnIcons,
  getColumnReportDate,
  getColumnReportDescription,
  getColumnReportName
} from 'modules/insights/sov/utils/tables/sov-tables';
import {
  getCommonModalProps,
  getModalLabels,
  getModalTitle
} from 'modules/insights/sov/utils/modals/sov-report-modal';

import './sov-reports.scss';

import type { Organization, SVGDynamicIconProps, SortableTableColumnType } from 'types';
import type {
  SOVGeneralOnCreate,
  SOVGeneralOnDelete,
  SOVGeneralOnDuplicate,
  SOVIconEnriched,
  SOVReport,
  SOVReportType
} from 'modules/insights/sov/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-reports.i18n.json'));

type Props = {
  organization: Organization,
  reports: Array<SOVReport>,
  reportType: SOVReportType,
  isLoading: boolean,
  isActionEvent: boolean,
  isFreemium: boolean | undefined | null,
  onCreateReport: SOVGeneralOnCreate,
  onDuplicateReport: SOVGeneralOnDuplicate,
  onDeleteReport: SOVGeneralOnDelete
};

const TooltipWithMetaModal = withModal(Tooltip, {
  modalContent: SOVModalMeta,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const TooltipWithGenericModal = withModal(Tooltip, {
  modalContent: SOVModalGeneric,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const AddNewWithMetaModal = withModal(AddNew, {
  modalContent: SOVModalMeta,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const COLLABARY_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_reports';

class SOVReports extends React.PureComponent<Props> {
  getIconView(report: SOVReport): Partial<SOVIconEnriched> {
    const { reportType } = this.props;
    const icon = getIconView(report.id, reportType);

    return enrichReportIcon(report, icon);
  }

  getIconDuplicate(report: SOVReport): Partial<SOVIconEnriched> {
    const { onDuplicateReport } = this.props;
    const icon = getIconDuplicate(onDuplicateReport);

    return enrichReportIcon(report, icon);
  }

  getIconDelete(report: SOVReport): Partial<SOVIconEnriched> {
    const { onDeleteReport } = this.props;
    const icon = getIconDelete(onDeleteReport);

    return enrichReportIcon(report, icon);
  }

  getColumns(): Array<SortableTableColumnType> {
    const { reports } = this.props;

    return [
      ...insertIf(!isEmpty(reports), ...[
        getColumnReportName(MSG_KEY, 'sov-reports__column-name'),
        getColumnReportDescription(MSG_KEY, 'sov-reports__column-description'),
        getColumnReportDate('sov-reports__column-date'),
        getColumnIcons(this.renderIconModal, 'sov-reports__column-icons')
      ])
    ];
  }

  getReportData(): Array<SOVReport> {
    const { reports } = this.props;

    return reports.map(report => ({
      ...report,
      icons: [
        this.getIconView(report),
        this.getIconDuplicate(report),
        this.getIconDelete(report)
      ]
    }));
  }

  renderIconModal = (icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode | undefined | null => {
    if (icon.name === ICONS_MAP.duplicate) {
      return this.renderModalDuplicate(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.delete) {
      return this.renderModalDelete(icon, iconProps);
    }

    return null;
  };

  renderModalDuplicate(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    const { onDuplicateReport, reportType } = this.props;

    return (
      <TooltipWithMetaModal
        modalContentProps={{
          ...getCommonModalProps(DUPLICATE, reportType),
          report: icon.report,
          onAction: onDuplicateReport
        }}
        message={icon.tooltip}
        width="micro"
      >
        <SVGDynamicIcon {...iconProps} />
      </TooltipWithMetaModal>
    );
  }

  renderModalDelete(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    const { reportType, onDeleteReport } = this.props;

    return (
      <TooltipWithGenericModal
        modalContentProps={{
          title: getModalTitle(DELETE, reportType),
          labels: getModalLabels(DELETE),
          message: __t(`${MSG_KEY}.messages.delete_report`, {
            reportName: icon.report?.name || UNTITLED,
            reportType: __t(`${COLLABARY_MSG_KEY}.social.${reportType}`)
          }),
          report: icon.report,
          reportType,
          sectionType: REPORT,
          actionType: DELETE,
          onAction: onDeleteReport
        }}
        message={icon.tooltip}
        width="micro"
      >
        <SVGDynamicIcon {...iconProps} />
      </TooltipWithGenericModal>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-reports__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderTeaserFreemium(): React.ReactNode {
    const { reports, reportType, onCreateReport } = this.props;

    return (
      <div className="sov-reports__freemium-teaser">
        <SOVTeaserFreemium
          reportProps={{
            ...getCommonModalProps(CREATE, reportType),
            onAction: onCreateReport
          }}
          hasReports={!isEmpty(reports)}
        />
      </div>
    );
  }

  renderAddNew(): React.ReactNode {
    const { reportType, onCreateReport } = this.props;

    return (
      <div className="sov-reports__add-new">
        <AddNewWithMetaModal
          modalContentProps={{
            ...getCommonModalProps(CREATE, reportType),
            onAction: onCreateReport
          }}
          text={__t(`${SHARED_MSG_KEY}.labels.add_new`)}
          tooltip={__t(`${MSG_KEY}.tooltips.add_new`)}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16
          }}
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="sov-reports__header">
        <div className="sov-reports__header-text">
          {__t(`${MSG_KEY}.labels.reports`)}
        </div>
        {this.renderAddNew()}
      </div>
    );
  }

  renderTableOverlay(): React.ReactNode {
    return (
      <div className="sov-reports__table-overlay">
        {this.renderSpinner()}
      </div>
    );
  }

  renderTable(): React.ReactNode {
    const { isLoading, isActionEvent } = this.props;
    const reportData = this.getReportData();
    const filteredReportData = reportData.filter(item => !isNil(item.name));
    const sortedReportData = orderBy(filteredReportData, ['createdAt', 'updatedAt'], ['desc', 'desc']);

    return (
      <React.Fragment>
        {
          isLoading && isActionEvent &&
            this.renderTableOverlay()
        }
        <SortableTable
          data={sortedReportData}
          columns={this.getColumns()}
          layout="simple"
          maxHeight={322}
        />
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { isLoading, isActionEvent, isFreemium } = this.props;
    const reportData = this.getReportData();

    const tableClasses = classnames('sov-reports__table', {
      'sov-reports__table--no-data': isEmpty(reportData)
    });

    return (
      <div className="sov-reports">
        <div className="sov-reports__list">
          {this.renderHeader()}
          <div className={tableClasses}>
            {
              isLoading && !isActionEvent
                ? this.renderSpinner()
                : this.renderTable()
            }
          </div>
        </div>
        {
          !isLoading && isFreemium &&
            this.renderTeaserFreemium()
        }
      </div>
    );
  }
}

export default SOVReports;
